"use client";

import Image, { type ImageProps } from "next/image";
import { useState } from "react";

interface FallbackImageProps extends Omit<ImageProps, "onError"> {
  fallbackClassName?: string;
}

// Brand-tinted 8x8 blur used as a generic placeholder for every product image.
// We don't store per-image blur hashes, so this soft cream→gold wash stands in
// for all of them — it renders instantly (inline base64) so there's never a
// blank box while the real image streams in. Pre-encoded literal (not built via
// Buffer, which isn't available in the browser for this client component).
const BRAND_BLUR_DATA_URL =
  "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI4IiBoZWlnaHQ9IjgiPjxkZWZzPjxsaW5lYXJHcmFkaWVudCBpZD0iZyIgeDE9IjAiIHkxPSIwIiB4Mj0iMSIgeTI9IjEiPjxzdG9wIG9mZnNldD0iMCIgc3RvcC1jb2xvcj0iI2Y3ZjFlNyIvPjxzdG9wIG9mZnNldD0iMSIgc3RvcC1jb2xvcj0iI2U3ZDNhZCIvPjwvbGluZWFyR3JhZGllbnQ+PC9kZWZzPjxyZWN0IHdpZHRoPSI4IiBoZWlnaHQ9IjgiIGZpbGw9InVybCgjZykiLz48L3N2Zz4=";

export default function FallbackImage({
  fallbackClassName,
  className,
  alt,
  placeholder,
  blurDataURL,
  ...props
}: FallbackImageProps) {
  const [error, setError] = useState(false);
  const [loaded, setLoaded] = useState(false);

  if (error) {
    return (
      <div
        className={
          fallbackClassName ||
          "absolute inset-0 bg-gradient-to-br from-brand-cream via-brand-gold/10 to-brand-cream"
        }
        role="img"
        aria-label={alt}
      />
    );
  }

  const effectiveBlur = blurDataURL ?? BRAND_BLUR_DATA_URL;

  return (
    <>
      {/* Shimmer only shows for the split second before the (inline base64) blur
          placeholder paints. We do NOT opacity-fade the <Image> itself — that
          would hide Next's native blur placeholder. Next handles the
          blur→sharp crossfade internally once the real image decodes. */}
      {!loaded && (
        <div
          className="absolute inset-0 bg-gradient-to-r from-brand-cream via-brand-gold/10 to-brand-cream animate-shimmer"
          style={{
            backgroundSize: "200% 100%",
          }}
        />
      )}
      <Image
        {...props}
        alt={alt}
        placeholder={placeholder ?? "blur"}
        blurDataURL={effectiveBlur}
        className={className}
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
      />
    </>
  );
}
